// Spec 2026-10-20 (restaurants edit their payout schedule, on every
// surface) §2.1. The one model of the payout schedule form: what the web's
// two editors — the restaurant's modal (payout-schedule-modal.js, opened
// from Overview) and the admin's form (company-detail/tabs/
// connect-payout-schedule.js) — hold, what they send to
// PUT Connect/PayoutSchedule, and when there is something to save. Pure,
// so each rule is pinned once rather than copied into both files; the
// Manager app carries the same rules in its ScheduleChoice.
//
// Delay days are never part of it: Stripe fixes them per country. Stripe
// accepts a monthly anchor of 1-31 and pays on the last day of a shorter
// month itself, so the day list is not trimmed per month.
export const INTERVALS = [
  { value: 'manual', label: 'Manual', every: null },
  { value: 'daily', label: 'Daily', every: 'day' },
  { value: 'weekly', label: 'Weekly', every: 'week' },
  { value: 'monthly', label: 'Monthly', every: 'month' },
];

// The three automatic cadences, as the modal's "Automatic every …"
// dropdown lists them.
export const CADENCES = INTERVALS.filter((i) => i.every).map((i) => ({ value: i.value, label: i.every }));

// Stripe names the weekly anchor in lowercase English.
export const WEEKDAYS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

export const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: String(i + 1) }));

// What a form holds, read off a balance. An interval Stripe did not name
// (null, or a word this list does not know) leaves nothing selected, so
// Save stays off until a choice is made; the anchors default so the second
// control has a value the moment its interval is chosen.
export const formOf = (balance) => ({
  interval: INTERVALS.some((i) => i.value === balance?.scheduleInterval) ? balance.scheduleInterval : '',
  weeklyAnchor: balance?.scheduleWeeklyAnchor || 'monday',
  monthlyAnchor: balance?.scheduleMonthlyAnchor || 1,
});

// Exactly the body ConnectPayoutScheduleRequest takes: lowercase names, and
// only the anchor the chosen interval uses — Stripe refuses a weekly anchor
// on a daily schedule, and a form keeps its controls' values while the
// interval changes, so a stale value is normal.
export const bodyOf = (form) => ({
  interval: form.interval,
  weeklyanchor: form.interval === 'weekly' ? form.weeklyAnchor : null,
  monthlyanchor: form.interval === 'monthly' ? Number(form.monthlyAnchor) : null,
});

// Something to save: a choice was made, and its body differs from the
// balance's — judged on the body, so a round trip through another interval
// with the anchor untouched lands back on "nothing changed".
export const changedFrom = (form, balance) =>
  !!form.interval && JSON.stringify(bodyOf(form)) !== JSON.stringify(bodyOf(formOf(balance)));
